import * as React from 'react'
import styles from './index.module.scss'
import gutterStyles from '../sx/spacing/index.module.scss'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import { SpaceClassModifierWithAuto } from '../sx/spacing'

type RowColWidth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 'auto'
type gutterSize = SpaceClassModifierWithAuto
type gutterDirection = 'x' | 'y'
type RowColumns =
  | RowColWidth
  | {
      col?: RowColWidth
      gutterSize?: gutterSize
      gutterDirection?: gutterDirection
    }

export interface RowProps {
  Component?: React.ElementType
  xs?: RowColumns
  sm?: RowColumns
  md?: RowColumns
  lg?: RowColumns
  xl?: RowColumns
  xxl?: RowColumns
  className?: string
  children: React.ReactNode
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

const breakpoints = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'] as const
const minBreakpoint = 'xs'
const prefix = 'row-cols'
export const Row = React.forwardRef<HTMLDivElement, RowProps>((props, ref) => {
  const {
    Component = 'div',
    className,
    children,
    xs,
    md,
    lg,
    xl,
    xxl,
    sm,
    sx,
    ...other
  } = props

  const _classes = React.useMemo(() => {
    const classes: string[] = []
    for (let itr = 0; itr < breakpoints.length; itr++) {
      const breakpoint = breakpoints[itr]
      const propValue = props[breakpoint!]
      if (!propValue) {
        continue
      }
      let col: RowColWidth | undefined
      let gutterSize: gutterSize | undefined
      let gutterDirection: gutterDirection | undefined

      if (propValue !== null && typeof propValue === 'object') {
        ;({ col, gutterSize, gutterDirection } = propValue)
      } else {
        col = propValue
      }
      const infix = breakpoint !== minBreakpoint ? `-${breakpoint}` : ''
      // here we use double eqality check against triple equality check to handle non null cases and zero
      if (col != null) {
        // @ts-ignore
        classes.push(`${styles[`${prefix}${infix}-${col}`]}`)
      }
      if (gutterSize != null) {
        classes.push(
          `${gutterStyles[`g${gutterDirection ?? ''}${infix}-${gutterSize}`]}`
        )
      }
    }
    return classes
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [xxl, xl, lg, md, xs, sm])

  return (
    <Component
      ref={ref}
      className={clsx(className, styles.row, sxFunc(sx), ..._classes)}
      {...other}
    >
      {children}
    </Component>
  )
})
Row.displayName = 'Row'
